import React from "react";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";
import { ZoneSubTypography, ZoneTypography } from "./ZoneTypography";
import RoomData from "../../../interfaces/robot/rooms/room-data";
import { getDefaultChargingStationMove, StationDrag, StationDragPatch } from "../../../interfaces/robot/rooms/station";
import ZoneControllerMouseState from "../../../enums/robot/zone-planner/zone-controller-mouse-state";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../store/SharedProvider";
import {
  selectIsAdvancedModeEnabled,
  selectIsWebApp,
} from "../../../store/app-configurations/app-configurations-selectors";
import { getSvgCoordinatesFromRosPoint } from "../../../interfaces/robot/rooms/point";
import IdTypePoint from "../../../interfaces/robot/rooms/id-type-point";
import ZonePointType from "../../../enums/robot/rooms/zone-point-type";
import { selectRooms } from "../../../store/room/room-selectors";
import { RotateIcon, ChargingStationIcon, CompleteIcon } from "../../../utils/icons/icons";
import MessageType from "../../../enums/robot/rsw/message-types";

const ChargingStation: React.FC<{
  zoomScale: number;
  roomData: RoomData;
  station: StationDragPatch;
  mouseState: ZoneControllerMouseState;
}> = ({ zoomScale, roomData, station, mouseState }) => {
  const { t } = useTranslation();

  const dispatch = useSharedDispatch();

  const { patchStationDrag, sendRobotWebsocketMessage } = useSharedActions();

  const isAdvancedModeEnabled = useSharedSelector((state) => selectIsAdvancedModeEnabled(state));
  const availableRooms = useSharedSelector((state) => selectRooms(state));
  const isWebapp = useSharedSelector((state) => selectIsWebApp(state));

  const stationPoint = {
    id: "primaryStation",
    coordinateX: station.position.coordinateX,
    coordinateY: station.position.coordinateY,
    type: ZonePointType.MapOrigin,
  } as IdTypePoint;

  const stationMouseTouchDownHandler = () => {
    if (isWebapp && isAdvancedModeEnabled && mouseState === ZoneControllerMouseState.Default) {
      dispatch(patchStationDrag({ isSelected: true, isDragging: true }));
    }
  };

  const dragStationMouseTouchDownHandler = () => {
    dispatch(patchStationDrag({ isDragging: true }));
  };

  const rotateStationClickHandler = () => {
    dispatch(
      sendRobotWebsocketMessage({
        type: MessageType.MoveStation,
        message: {
          ...station.position,
          coordinateTheta: station.position.coordinateTheta + 90 > 270 ? 0 : station.position.coordinateTheta + 90,
          roomId: roomData.id,
        },
      }),
    );
    dispatch(
      patchStationDrag({
        position: {
          ...station.position,
          coordinateTheta: station.position.coordinateTheta + 90 > 270 ? 0 : station.position.coordinateTheta + 90,
        },
        unsavedChanges: true,
      }),
    );
  };

  const endMoveStationClickHandler = () => {
    dispatch(patchStationDrag({ ...getDefaultChargingStationMove(roomData.primaryStation) }));
  };

  const sinTheta = Math.sin((-station.position.coordinateTheta * Math.PI) / 180);
  const cosTheta = Math.cos((-station.position.coordinateTheta * Math.PI) / 180);
  const roomsInFloor = availableRooms.filter(
    (r) => r.floorId === availableRooms.find((room) => room.id === roomData.id)?.floorId,
  ).length;

  return (
    <React.Fragment>
      {station.isSelected ? (
        <React.Fragment>
          <RotateIcon
            viewBox="0 0 24 24"
            x={getSvgCoordinatesFromRosPoint(stationPoint, roomData)!.x - 30}
            y={getSvgCoordinatesFromRosPoint(stationPoint, roomData)!.y - 6}
            width={12}
            height={12}
            sx={{
              width: "12px",
              height: "12px",
            }}
            preserveAspectRatio="xMidYMid meet"
          />
          <Tooltip
            title={
              station.isDragging ? undefined : (
                <React.Fragment>
                  <ZoneTypography variant="body2" noWrap>
                    {t("station")}
                  </ZoneTypography>
                  {isAdvancedModeEnabled && <ZoneSubTypography>{t("station-click-to-rotate")}</ZoneSubTypography>}
                </React.Fragment>
              )
            }
          >
            <circle
              cx={getSvgCoordinatesFromRosPoint(stationPoint, roomData)!.x - 24}
              cy={getSvgCoordinatesFromRosPoint(stationPoint, roomData)!.y}
              r={5}
              onClick={rotateStationClickHandler}
              style={{ opacity: 0, cursor: "pointer" }}
            />
          </Tooltip>

          <CompleteIcon
            viewBox="0 0 24 24"
            color="success"
            x={getSvgCoordinatesFromRosPoint(stationPoint, roomData)!.x + 15}
            y={getSvgCoordinatesFromRosPoint(stationPoint, roomData)!.y - 6}
            width={12}
            height={12}
            sx={{
              width: "12px",
              height: "12px",
            }}
            preserveAspectRatio="xMidYMid meet"
          />
          <Tooltip
            title={
              station.isDragging ? undefined : (
                <React.Fragment>
                  <ZoneTypography variant="body2" noWrap>
                    {t("info-tooltip-close")}
                  </ZoneTypography>
                </React.Fragment>
              )
            }
          >
            <circle
              cx={getSvgCoordinatesFromRosPoint(stationPoint, roomData)!.x + 22}
              cy={getSvgCoordinatesFromRosPoint(stationPoint, roomData)!.y}
              r={5}
              onClick={endMoveStationClickHandler}
              style={{ opacity: 0, cursor: "pointer" }}
            />
          </Tooltip>

          <Tooltip
            title={
              station.isDragging ? undefined : (
                <React.Fragment>
                  <ZoneTypography variant="body2" noWrap>
                    {t("station")}
                  </ZoneTypography>
                  {isAdvancedModeEnabled && <ZoneSubTypography>{t("station-click-to-drag")}</ZoneSubTypography>}
                </React.Fragment>
              )
            }
          >
            <svg>
              <circle
                cx={
                  getSvgCoordinatesFromRosPoint(stationPoint, roomData)!.x -
                  6 * Math.cos(((-station.position.coordinateTheta + 45) * Math.PI) / 180)
                }
                cy={
                  getSvgCoordinatesFromRosPoint(stationPoint, roomData)!.y -
                  6 * Math.sin(((-station.position.coordinateTheta + 45) * Math.PI) / 180)
                }
                r={2}
                style={{ cursor: "grab" }}
                onMouseDown={dragStationMouseTouchDownHandler}
                onTouchStart={dragStationMouseTouchDownHandler}
              />

              <circle
                cx={
                  getSvgCoordinatesFromRosPoint(stationPoint, roomData)!.x -
                  6 * Math.cos(((-station.position.coordinateTheta - 45) * Math.PI) / 180)
                }
                cy={
                  getSvgCoordinatesFromRosPoint(stationPoint, roomData)!.y -
                  6 * Math.sin(((-station.position.coordinateTheta - 45) * Math.PI) / 180)
                }
                r={2}
                style={{ cursor: "grab" }}
                onMouseDown={dragStationMouseTouchDownHandler}
                onTouchStart={dragStationMouseTouchDownHandler}
              />
              <circle
                cx={getSvgCoordinatesFromRosPoint(stationPoint, roomData)!.x - 8 * cosTheta}
                cy={getSvgCoordinatesFromRosPoint(stationPoint, roomData)!.y - 8 * sinTheta}
                r={5}
                style={{ cursor: "grab", fill: "#007CBD" }}
                onMouseDown={dragStationMouseTouchDownHandler}
                onTouchStart={dragStationMouseTouchDownHandler}
              />
              <line
                x1={
                  getSvgCoordinatesFromRosPoint(stationPoint, roomData)!.x -
                  8 * Math.cos(-((90 - station.position.coordinateTheta) * Math.PI) / 180)
                }
                y1={
                  getSvgCoordinatesFromRosPoint(stationPoint, roomData)!.y -
                  8 * Math.sin(-((90 - station.position.coordinateTheta) * Math.PI) / 180)
                }
                x2={
                  getSvgCoordinatesFromRosPoint(stationPoint, roomData)!.x -
                  8 * Math.cos(((station.position.coordinateTheta + 90) * Math.PI) / 180)
                }
                y2={
                  getSvgCoordinatesFromRosPoint(stationPoint, roomData)!.y -
                  8 * Math.sin(((station.position.coordinateTheta + 90) * Math.PI) / 180)
                }
                style={{
                  filter: "drop-shadow(0 0 1px #000000)",
                  stroke: "#FFC400",
                  strokeLinejoin: "round",
                  cursor: station.isDragging ? undefined : "grab",
                  strokeWidth: 4,
                }}
                onMouseDown={dragStationMouseTouchDownHandler}
                onTouchStart={dragStationMouseTouchDownHandler}
              />
            </svg>
          </Tooltip>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <Tooltip
            title={
              <React.Fragment>
                <ZoneTypography variant="body2" noWrap>
                  {t("station")}
                </ZoneTypography>
                {isAdvancedModeEnabled && (
                  <ZoneSubTypography>
                    {!isWebapp
                      ? t("move-station-webapp-only")
                      : roomsInFloor === 1
                        ? t("station-click-to-select")
                        : t("remove-room-from-floor-to-move-station")}
                  </ZoneSubTypography>
                )}
              </React.Fragment>
            }
          >
            <ChargingStationIcon
              viewBox={`0 0 ${roomData.mapInformation.width * 0.4 * zoomScale} ${
                roomData.mapInformation.height * 0.4 * zoomScale
              }`}
              x={getSvgCoordinatesFromRosPoint(stationPoint, roomData)!.x - 12}
              y={getSvgCoordinatesFromRosPoint(stationPoint, roomData)!.y - 12}
              onMouseDown={roomsInFloor === 1 ? stationMouseTouchDownHandler : undefined}
              onTouchStart={roomsInFloor === 1 ? stationMouseTouchDownHandler : undefined}
              width={roomData.mapInformation.width * 0.4 * zoomScale}
              height={roomData.mapInformation.height * 0.4 * zoomScale}
              sx={{
                cursor: isAdvancedModeEnabled && isWebapp ? "grab" : undefined,
                width: `${roomData.mapInformation.width * 0.4 * zoomScale}px`,
                height: `${roomData.mapInformation.height * 0.4 * zoomScale}px`,
              }}
              preserveAspectRatio="xMidYMid meet"
            />
          </Tooltip>
        </React.Fragment>
      )}
      <circle
        cx={getSvgCoordinatesFromRosPoint(stationPoint, roomData)!.x}
        cy={getSvgCoordinatesFromRosPoint(stationPoint, roomData)!.y}
        r={0.7}
      />
    </React.Fragment>
  );
};

export default ChargingStation;
